import axios from 'axios';
import store from '@/store';

class APIService
{
	constructor()
	{
		// Initialize error tracking variables
		this.apiErrors = false;
		this.apiErrorMessages = [];
	}

	/**
	 * Performs an HTTP request using the specified method, path, and data.
	 * Displays an overlay during the request and handles errors consistently.
	 *
	 * @param {Object} options - The options for the API request.
	 * @param {string} options.method - The HTTP method (e.g., 'get', 'post', 'put', 'delete').
	 * @param {string} options.path - The endpoint path, relative to the API base URL.
	 * @param {Object} [options.data] - The payload data for 'post' or 'put' requests.
	 * @returns {Object} - The response data or an error object if the request fails.
	 */
	async fetch({ method, path, data })
	{
		// Construct full URL using the root from the store
		const url = `${store.state.root}/laravel/public/api/v1/${path}`;

		// Configure the request with headers and method
		const config = {
			method,
			url,
			data,
			headers: {
				'Content-Type': 'application/json;charset=UTF-8',
			},
		};

		// Reset error state before each request
		this.apiErrors = false;
		this.apiErrorMessages = [];

		// Activate loading overlay
		store.commit('TOGGLE_OVERLAY', true);

		try
		{
			// Perform the request and return response data directly
			return await axios.request(config).then(response => response.data);
		} catch (error)
		{
			// Handle errors consistently and return structured error message
			this.handleError(error);
			return { error: this.apiErrorMessages };
		} finally
		{
			// Deactivate loading overlay after request completes
			store.commit('TOGGLE_OVERLAY', false);
		}
	}

	/**
	 * Handles API request errors, setting the appropriate error messages
	 * and performing actions such as redirection when needed.
	 *
	 * @param {Object} error - The error object from axios.
	 */
	handleError(error)
	{
		if (!error.response)
		{
			this.apiErrors = true;
			this.apiErrorMessages = 'Network Error or CORS issue';
			return;
		}

		const status = error.response.status;
		this.apiErrors = true;

		// Set error messages based on HTTP status code
		switch (status)
		{
			case 401:
				// Redirect to login if unauthorized
				window.location.href = `${store.state.root}/login?message=You%20are%20logged%20out!`;
				break;
			case 400:
				this.apiErrorMessages = error.response.data;
				break;
			case 404:
				this.apiErrorMessages = 'Not Found';
				break;
			case 409:
				this.apiErrorMessages = error.response.data[0].message;
				break;
			case 418:
				this.apiErrorMessages = error.response.data;
				break;
			default:
				this.apiErrorMessages = 'Unknown error occurred';
				break;
		}
	}
}

export default new APIService();
